import React, { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useFormContext } from 'react-hook-form';
import { Typography, Grid } from '@mui/material';
import FormTextField from '../../../../../Components/Form/FormTextField';
import PercentRoundedIcon from '@mui/icons-material/PercentRounded';
import EuroRoundedIcon from '@mui/icons-material/EuroRounded';

const monetaryFields = [
  'arrangementFees',
  'netAmount',
  'stamp',
  'externalDebtConsolidation',
  'internalDebtConsolidation',
  'insurancePremiumAmountBundle',
  'insurancePremiumAmountCPI',
  'identinet',
];

const toCents = (value) => {
  const normalizedValue = `${value ?? 0}`.trim().replace(',', '.');

  return Math.round(Number(normalizedValue || 0) * 100);
};

const useStyles = () => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    gap: '18px',
  },
});

//last page containing optional checkboxes and optional notes on the current practice
const ProductPage = function ProductPage({ errors = null, formData }) {
  const intl = useIntl();
  const classes = useStyles();
  const { reset, getValues } = useFormContext();

  useEffect(() => {
    reset({
      ...formData,
      stamp: formData?.stamp ?? '0',
      netAmount: formData?.netAmount ?? '0',
      requiredAmount: formData?.requiredAmount ?? '0',
      arrangementFees: formData?.arrangementFees ?? '0',
      externalDebtConsolidation: formData?.externalDebtConsolidation ?? '0',
      internalDebtConsolidation: formData?.internalDebtConsolidation ?? '0',
      insurancePremiumAmountBundle:
        formData?.insurancePremiumAmountBundle ?? '0',
      insurancePremiumAmountCPI: formData?.insurancePremiumAmountCPI ?? '0',
      identinet: formData?.identinet ?? '0',
    });
  }, []);

  return (
    <div style={classes.root}>
      <Typography variant="h6">{intl.messages.FINANCIAL_PLAN}</Typography>
      <Grid container rowSpacing="18px" columnSpacing="16px">
        <Grid size={{ md: 5, sm: 4, xs: 4 }}>
          <FormTextField
            type="number"
            name="requiredAmount"
            error={errors?.requiredAmount}
            defaultValue={formData?.requiredAmount ?? '0'}
            icon={<EuroRoundedIcon />}
            required
            validateCallback={(amount) => {
              if (isNaN(amount)) {
                return intl.messages.NOLETTERS_VALIDATION;
              }

              const requiredAmount = toCents(amount);
              const otherAmounts = monetaryFields.reduce(
                (total, fieldName) => total + toCents(getValues(fieldName)),
                0
              );

              return (
                requiredAmount === otherAmounts ||
                intl.messages.REQUIREDAMOUNT_SUM_VALIDATION
              );
            }}
          />
        </Grid>
        <Grid size={{ md: 2, sm: 4, xs: 4 }}>
          <FormTextField
            name="duration"
            error={errors?.duration}
            type="number"
            required
            validateCallback={(dur) =>
              !isNaN(dur) || intl.messages.NOLETTERS_VALIDATION
            }
          />
        </Grid>
        <Grid size={{ md: 2, sm: 4, xs: 4 }}>
          <FormTextField
            name="rate"
            error={errors?.rate}
            type="number"
            icon={<PercentRoundedIcon />}
            required
            validateCallback={(rate) =>
              !isNaN(rate) || intl.messages.NOLETTERS_VALIDATION
            }
          />
        </Grid>
        <Grid size={{ md: 3, sm: 4, xs: 4 }}>
          <FormTextField
            type="number"
            name="arrangementFees"
            error={errors?.arrangementFees}
            icon={<EuroRoundedIcon />}
            required
            validateCallback={(amount) =>
              !isNaN(amount) || intl.messages.NOLETTERS_VALIDATION
            }
          />
        </Grid>
        <Grid size={{ md: 3, sm: 4, xs: 4 }}>
          <FormTextField
            type="number"
            name="netAmount"
            required
            error={errors?.netAmount}
            icon={<EuroRoundedIcon />}
            validateCallback={(amount) =>
              !isNaN(amount) || intl.messages.NOLETTERS_VALIDATION
            }
          />
        </Grid>
        <Grid size={{ md: 3, sm: 4, xs: 4 }}>
          <FormTextField
            type="number"
            name="stamp"
            required
            error={errors?.stamp}
            icon={<EuroRoundedIcon />}
            validateCallback={(amount) =>
              !isNaN(amount) || intl.messages.NOLETTERS_VALIDATION
            }
          />
        </Grid>
        <Grid size={{ md: 3, sm: 4, xs: 4 }}>
          <FormTextField
            type="number"
            name="externalDebtConsolidation"
            required
            error={errors?.externalDebtConsolidation}
            icon={<EuroRoundedIcon />}
            validateCallback={(amount) =>
              !isNaN(amount) || intl.messages.NOLETTERS_VALIDATION
            }
          />
        </Grid>
        <Grid size={{ md: 3, sm: 4, xs: 4 }}>
          <FormTextField
            type="number"
            name="internalDebtConsolidation"
            required
            error={errors?.internalDebtConsolidation}
            icon={<EuroRoundedIcon />}
            validateCallback={(amount) =>
              !isNaN(amount) || intl.messages.NOLETTERS_VALIDATION
            }
          />
        </Grid>
        <Grid size={{ md: 4, sm: 4, xs: 4 }}>
          <FormTextField
            type="number"
            name="insurancePremiumAmountBundle"
            required
            error={errors?.insurancePremiumAmountBundle}
            icon={<EuroRoundedIcon />}
            validateCallback={(amount) =>
              !isNaN(amount) || intl.messages.NOLETTERS_VALIDATION
            }
          />
        </Grid>
        <Grid size={{ md: 4, sm: 4, xs: 4 }}>
          <FormTextField
            type="number"
            name="insurancePremiumAmountCPI"
            required
            error={errors?.insurancePremiumAmountCPI}
            icon={<EuroRoundedIcon />}
            validateCallback={(amount) =>
              !isNaN(amount) || intl.messages.NOLETTERS_VALIDATION
            }
          />
        </Grid>
        <Grid size={{ md: 4, sm: 4, xs: 4 }}>
          <FormTextField
            type="number"
            name="identinet"
            required
            error={errors?.identinet}
            icon={<EuroRoundedIcon />}
            validateCallback={(amount) =>
              !isNaN(amount) || intl.messages.NOLETTERS_VALIDATION
            }
          />
        </Grid>
        <Grid size={{ md: 12, sm: 8, xs: 12 }}>
          <FormTextField name="notes" multiline />
        </Grid>
      </Grid>
    </div>
  );
};

export default ProductPage;
